import React from "react";
import { useDrag } from "@use-gesture/react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { useSpring, animated, to } from "react-spring";
import useStyles from "./DragDropControl.styles";
import { useElementDimensions } from "../../hooks/useElementDimensions";
import { ControlProps } from "./BaseControl";
import { useMentionValues } from "../../hooks/useMentions";
import useRecall from "../../hooks/useRecall";
import useTextDirection from "../../hooks/useTextDirection";
import { ChoiceModel } from "../../models/ChoiceModel";
import { QuestionModel } from "../../models/QuestionModel";
import { VariableModel } from "../../models/VariableModel";
import { translate } from "../../models/utilities/translationUtilities";
import { useKeyboardNavigation } from "../../hooks/useKeyboardNavigation";
import { NumericRankingNavigation } from "./KeyboardNavigation/NumericRankingNavigation";
import { useLanguage } from "../../hooks/useLanguage";
import { useValidation, useDiscreteAnswerValidator } from "../../hooks/useValidation";
import { usePrevious } from "../../hooks/usePrevious";
import { isIE } from "../../../src/utils";
import { useMouseHover } from "../../hooks/useMouseHover";
import { shouldUseModernTheme } from "../../theme/navigationButtonThemeColors";
import * as ChoiceTokens from "../../theme/choiceThemeTokens";
import { Theme } from "../../types/Theme";

const BUCKET_DIVIDER_SIZE = 16;
const MIN_CARD_HEIGHT = 47;
const CARD_SPACING = 15;
const BOUNDS_MARGIN = 90;

const ORIGIN = 0;
const DESTINATION = 1;

const insert = (l, i, x) => [...l.slice(0, x), i, ...l.slice(x)];

const getModernTextCardFallbackHeight = (theme: Theme): number => {
    const [verticalPadding] = ChoiceTokens.resolveChoicePaddingModern(theme)[0];
    return (
        Number(theme.TextFontSize) +
        verticalPadding * 2 +
        Math.max(theme.BorderWidth, 1) * 2
    );
};

interface CardProps {
    id?: string;
    text: string;
    imageUrl: string;
    width: number;
    height: number;
    top: number;
    left: number;
    bounds: { top: number; bottom: number; left: number; right: number };
    onDrag(xy: number[]): void;
    onDrop(xy: number[]): void;
    setCardHeight(id: string, height: number): void;
    isDragAllowed: boolean;
    selected?: boolean;
    textOnly?: boolean;
    question: QuestionModel;
    variable: VariableModel;
}

function DragDropCard({
    id,
    text,
    imageUrl,
    width,
    height,
    top,
    left,
    onDrag,
    onDrop,
    bounds,
    setCardHeight,
    isDragAllowed,
    selected = false,
    textOnly = false,
    question,
    variable,
}: CardProps) {
    const [hover, pointerHandlers] = useMouseHover();
    const ref = React.useRef<HTMLDivElement | undefined>();
    const { elementReference, onKeyDown } = useKeyboardNavigation(
        id,
        variable,
        question,
        NumericRankingNavigation,
    );
    const recalledImage = useRecall(imageUrl);
    const recalledText = useRecall(text);
    const prevBounds = usePrevious(bounds);
    const theme = useTheme() as Theme;
    const useModernTheme = shouldUseModernTheme(theme);
    const classes = useStyles({ theme, selected, textOnly });
    const boundsChanged = prevBounds !== bounds;

    // Track the initial drag position to calculate absolute position
    const dragStartPos = React.useRef<[number, number] | null>(null);
    // During drag, position from state so it's not delayed by spring's async update (fixes lag)
    const [liveDrag, setLiveDrag] = React.useState<[number, number] | null>(null);

    const [spring, controller] = useSpring(
        () => ({
            x: left,
            y: top,
            z: 0,
            immediate: (name: string) => boundsChanged && ["x", "y"].includes(name),
            config: { mass: 1, tension: 170, friction: 25 },
        }),
        [left, top, boundsChanged],
    );

    // Only update position when not dragging
    React.useLayoutEffect(() => {
        if (dragStartPos.current === null) {
            controller.set({
                x: left,
                y: top,
                z: spring.z.get(),
            });
        }
    }, [left, top, controller, spring.z]);

    const isLegacyBrowser = isIE();

    const bind = useDrag(
        (state) => {
            if (isDragAllowed) return;
            const { movement, down } = state;

            if (down && dragStartPos.current === null) {
                // Capture the starting position when drag begins
                dragStartPos.current = [spring.x.get(), spring.y.get()];
            }

            if (!down) {
                // Reset when drag ends
                dragStartPos.current = null;
            }

            // Calculate absolute position: start position + movement offset
            const absoluteX = dragStartPos.current ? dragStartPos.current[0] + movement[0] : left;
            const absoluteY = dragStartPos.current ? dragStartPos.current[1] + movement[1] : top;

            if (down) {
                setLiveDrag([absoluteX, absoluteY]);
            } else {
                setLiveDrag(null);
            }

            // Keep spring in sync (for z lift effect and for snap-back on release)
            const update = {
                x: down ? absoluteX : left,
                y: down ? absoluteY : top,
                z: down ? 10 : 0,
            };
            if (down) {
                (controller as { start: (o: object) => void }).start({
                    ...update,
                    immediate: true,
                });
            } else {
                controller.set(update);
            }

            if (down && onDrag) onDrag(movement);
            if (!down && onDrop) onDrop(movement);
        },
        {
            from: () => [spring.x.get(), spring.y.get()],
            filterTaps: true,
            bounds: {
                ...bounds,
                right: bounds.right - width,
                bottom: bounds.bottom - height,
            },
        },
    );

    // Update card height when width changes
    React.useLayoutEffect(() => {
        if (ref && ref.current) {
            setCardHeight(id, ref.current.clientHeight);
        }
    }, [id, ref, width]);

    const setCardRef = (node: HTMLDivElement | null) => {
        ref.current = node ?? undefined;
        elementReference.current = node;
    };

    return (
        <animated.div
            id={id}
            ref={setCardRef}
            tabIndex={0}
            aria-disabled={isDragAllowed || undefined}
            onKeyDown={onKeyDown}
            style={{
                left: CARD_SPACING,
                top: CARD_SPACING * 2,
                width: width - CARD_SPACING * 2,
                ...(useModernTheme && textOnly ? {} : { minHeight: height }),
                transform:
                    liveDrag !== null
                        ? `translate(${liveDrag[0]}px, ${liveDrag[1]}px) scale(1.1)`
                        : to(
                              [spring.x, spring.y, spring.z],
                              (x, y, z) => `translate(${x}px, ${y}px) scale(${z * 0.01 + 1})`,
                          ),
                boxShadow:
                    liveDrag !== null
                        ? "0 3px 13px rgba(0,0,0,0.2)"
                        : spring.z.to((z) =>
                              z > 0 ? `0 ${z * 0.3}px ${z + 3}px rgba(0,0,0,0.2)` : undefined,
                          ),
                zIndex: liveDrag !== null ? 11 : spring.z.to((z) => Math.max(1, 1 + Math.round(z))),
                backgroundImage: recalledImage && `url('${recalledImage}')`,
                cursor: isLegacyBrowser && "pointer",
            }}
            {...(isDragAllowed ? {} : bind())}
            {...(isDragAllowed ? {} : pointerHandlers)}
            className={classnames(
                classes.card,
                useModernTheme && textOnly && classes.cardTextModern,
                useModernTheme && classes.cardModernFocus,
                !isDragAllowed && useModernTheme && hover && liveDrag === null && classes.cardHover,
                !isDragAllowed && !useModernTheme && hover && classes.cardActive,
                useModernTheme && liveDrag !== null && classes.cardDragging,
                selected && !isDragAllowed && classes.cardSelected,
                isDragAllowed && classes.cardNoDrag,
            )}
            dangerouslySetInnerHTML={recalledImage ? undefined : { __html: recalledText }}
            aria-label={recalledText}
        />
    );
}

interface BucketProps {
    origin?: boolean;
    destination?: boolean;
    width: number;
    height: number;
    left: number;
    top: number;
    isCursorNoDrag?: boolean;
}

export function DragDropBucket({
    origin,
    destination,
    width,
    height,
    left,
    top,
    isCursorNoDrag,
}: BucketProps) {
    const theme = useTheme();
    const classes = useStyles({ theme });

    return (
        <div
            className={classnames(
                classes.bucket,
                origin && classes.bucketOrigin,
                isCursorNoDrag && classes.bucketOriginNoDrag,
                destination && classes.bucketDestination,
            )}
            style={{ width, height, left, top }}
        />
    );
}

export function DragDropControl(props: ControlProps) {
    const { question, sequencedVariables, showSoftPrompt, validateSignal, max } = props;
    const variable = sequencedVariables[0];
    const [mentions, setMentions] = useMentionValues(variable.name);
    const [dragging, setDragging] = React.useState<ChoiceModel>();
    const [dropBucket, setDropBucket] = React.useState(0);
    const [dropIndex, setDropIndex] = React.useState(0);
    const dropTargetRef = React.useRef({ bucket: 0, index: 0 });
    const [language] = useLanguage();
    const theme = useTheme();
    const useModernTheme = shouldUseModernTheme(theme as Theme);
    const dir = useTextDirection();
    const [imageRatioByCard, setImageRatioByCard] = React.useState<{ [name: string]: number }>({});
    const classes = useStyles({ theme });
    const {
        ref: containerRef,
        dimensions: { innerWidth },
        calcDimensions,
    } = useElementDimensions<HTMLDivElement>();
    const [cardsHeights, setCardsHeights] = React.useState<{ [id: string]: number }>({});
    React.useLayoutEffect(calcDimensions, [cardsHeights]);

    const bucketWidth = innerWidth / 2 - BUCKET_DIVIDER_SIZE;

    const choiceHasImage = (choice: ChoiceModel) =>
        Boolean(translate(choice, "imageurl", language));

    const getCardHeight = (choice: ChoiceModel) => {
        const measured = cardsHeights[choice.elementId] || 0;
        if (useModernTheme && !choiceHasImage(choice)) {
            return measured || getModernTextCardFallbackHeight(theme as Theme);
        }
        return Math.max(
            bucketWidth / imageRatioByCard[choice.name] || 0,
            MIN_CARD_HEIGHT,
            measured,
        );
    };

    let [originChoices, destinationChoices] = [
        variable.choiceList
            .filter(({ value }) => !mentions.includes(value))
            .filter((choice) => choice !== dragging),
        mentions
            .map((value) => variable.choiceList.find((c) => c.value === value))
            .filter(Boolean)
            .filter((choice) => choice !== dragging),
    ];

    const onChoiceDrag =
        (choice: ChoiceModel) =>
        ([movementX, movementY]: number[]) => {
            setDragging(choice);
            const cardLeft = isChoiceSelected(choice) ? destinationLeft : originLeft;
            const destListActualOrder =
                mentions.length > 0
                    ? (mentions
                          .map((v) => variable.choiceList.find((c) => c.value === v))
                          .filter(Boolean) as ChoiceModel[])
                    : [];
            const cardTop = isChoiceSelected(choice)
                ? getCardTop(destListActualOrder, choice)
                : getCardTop(originChoices, choice);
            const absoluteX = cardLeft + movementX;
            const absoluteY = cardTop + movementY;
            const draggedCenterY = absoluteY + getCardHeight(choice) / 2;
            const bucket = absoluteX < innerWidth / 2 ? leftBucket : rightBucket;
            const choices =
                bucket === ORIGIN
                    ? originChoices
                    : destListActualOrder.length > 0
                      ? destListActualOrder
                      : destinationChoices;
            const choicesWithoutDragged = choices.filter((c) => c !== choice);
            const index = getDropIndex(choicesWithoutDragged, draggedCenterY);
            dropTargetRef.current = { bucket, index };
            setDropBucket(bucket);
            setDropIndex(index);
        };

    if (dragging && dropBucket === ORIGIN) {
        originChoices = insert(originChoices, dragging, dropIndex);
    }

    if (dragging && dropBucket === DESTINATION) {
        destinationChoices = insert(destinationChoices, dragging, dropIndex);
    }

    const getDestinationCardTop = (choice: ChoiceModel) =>
        getCardTop(destinationChoices, choice);

    const originBucketHeight =
        variable.choiceList
            .map((choice) => getCardHeight(choice) + CARD_SPACING)
            .reduce((a, b) => a + b) +
        CARD_SPACING * 3;

    let dynamicArrayOfChoices: ChoiceModel[] = [];

    if (destinationChoices.length < Number(max) || max === undefined || Number(max) === 0) {
        dynamicArrayOfChoices = variable.choiceList;
    } else {
        dynamicArrayOfChoices = destinationChoices;
    }

    const destinationBucketHeight =
        dynamicArrayOfChoices
            .map((choice, index) => {
                return Number(max) > index ? getCardHeight(choice) + CARD_SPACING : 0;
            })
            .reduce((a, b) => a + b) +
        CARD_SPACING * 3;

    const isChoiceSelected = ({ value }) => mentions && mentions.includes(value);

    const getCardTop = (list, choice) =>
        list
            .slice(0, list.indexOf(choice))
            .map((c) => getCardHeight(c) + CARD_SPACING)
            .reduce((sum, x) => sum + x, 0);

    const getDropIndex = (list, y) => {
        const fl = list.filter((c) => c !== dragging);
        return fl.filter((c) => y > getCardTop(fl, c) + getCardHeight(c) / 2).length;
    };

    const borderWidth = 1;
    const [leftBucket, rightBucket, originLeft, destinationLeft] = (() => {
        const left = bucketWidth + BUCKET_DIVIDER_SIZE * 2 - borderWidth * 2;
        if (dir === "ltr") {
            return [ORIGIN, DESTINATION, 0, left];
        } else {
            return [DESTINATION, ORIGIN, left, 0];
        }
    })();

    const bounds = React.useMemo(
        () => ({
            top: -BOUNDS_MARGIN,
            bottom: originBucketHeight + BOUNDS_MARGIN,
            left: -BOUNDS_MARGIN,
            right: innerWidth + BOUNDS_MARGIN,
        }),
        [innerWidth, originBucketHeight],
    );

    useValidation(validateSignal!, useDiscreteAnswerValidator(question, variable, showSoftPrompt));

    React.useEffect(
        () =>
            variable.choiceList.forEach((card) => {
                const imageUrl = translate(card, "imageurl", language);
                if (imageUrl) {
                    const { name } = card;
                    const img = new Image();
                    img.addEventListener("load", () => {
                        const ratio = img.naturalWidth / img.naturalHeight;
                        setImageRatioByCard((state) => ({ ...state, [name]: ratio }));
                    });
                    img.src = imageUrl;
                }
            }),
        [variable.choiceList, language],
    );

    const onChoiceDrop = (choice: ChoiceModel) => () => {
        const { value } = choice;
        const { bucket, index } = dropTargetRef.current;
        if (bucket === DESTINATION) {
            setMentions((b) => b.removeValue(value).insert(index, value));
        } else {
            setMentions((b) => b.removeValue(value));
        }
        setDragging(undefined);
    };

    const setCardHeight = (id: string, height: number) => {
        setCardsHeights((cards) => ({ ...cards, [id]: height }));
    };

    const maxAnswers = Number.parseInt(max, 10);

    const dropPlaceholderStyle = (() => {
        if (!dragging || !useModernTheme) {
            return null;
        }

        const bucketLeft = dropBucket === ORIGIN ? originLeft : destinationLeft;
        const list =
            dropBucket === ORIGIN
                ? variable.choiceList
                      .filter(({ value }) => !mentions.includes(value))
                      .filter((choice) => choice !== dragging)
                : (mentions
                      .map((value) => variable.choiceList.find((c) => c.value === value))
                      .filter(Boolean) as ChoiceModel[]).filter((choice) => choice !== dragging);

        const insertTop = list
            .slice(0, dropIndex)
            .map((choice) => getCardHeight(choice) + CARD_SPACING)
            .reduce((sum, height) => sum + height, 0);

        return {
            left: bucketLeft + CARD_SPACING,
            top: CARD_SPACING * 2 + insertTop,
            width: bucketWidth - CARD_SPACING * 2,
            height: getCardHeight(dragging),
        };
    })();

    return (
        <div
            className={classes.container}
            ref={containerRef}
            style={{ height: originBucketHeight }}
        >
            <DragDropBucket
                origin
                width={bucketWidth}
                height={originBucketHeight}
                left={originLeft}
                top={0}
                isCursorNoDrag={destinationChoices?.length >= maxAnswers}
            />
            <DragDropBucket
                destination
                width={bucketWidth}
                height={max === undefined ? originBucketHeight : destinationBucketHeight}
                left={destinationLeft}
                top={0}
            />

            {dropPlaceholderStyle && (
                <div
                    className={classes.dropPlaceholder}
                    style={dropPlaceholderStyle}
                    aria-hidden
                />
            )}

            {variable.choiceList.map((choice) => (
                <DragDropCard
                    question={question}
                    variable={variable}
                    isDragAllowed={
                        originChoices.includes(choice) && destinationChoices?.length >= maxAnswers
                    }
                    selected={isChoiceSelected(choice)}
                    id={choice.elementId}
                    key={choice.name}
                    text={translate(choice, "text", language)}
                    imageUrl={translate(choice, "imageurl", language)}
                    textOnly={useModernTheme && !choiceHasImage(choice)}
                    width={bucketWidth}
                    left={isChoiceSelected(choice) ? destinationLeft : originLeft}
                    height={getCardHeight(choice)}
                    top={
                        isChoiceSelected(choice)
                            ? getDestinationCardTop(choice)
                            : getCardTop(originChoices, choice)
                    }
                    bounds={bounds}
                    onDrag={onChoiceDrag(choice)}
                    onDrop={onChoiceDrop(choice)}
                    setCardHeight={setCardHeight}
                />
            ))}
        </div>
    );
}
